import { useCallback } from 'react';
import { noop } from 'lodash';
import Sentry from 'utils/sentry';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import useFetch, { muteAllButThrows } from 'hooks/useFetch';
import { LoadingAppViewModal } from 'design-system/LoadingAppView';
import { topUpSecclAccountFromBankAccountUsingDirectDebit, } from 'features/pay/actions';
import { getDestination } from '../utils/format';
import createTopUpErrorHandler from '../utils/createTopUpErrorHandler';
import { fetchWealthAccounts, pollPendingTopupsForTradingAccountId } from '../actions';
import { selectConnectedAccountWithBankId } from './selectors';
const useInvestCashToTradingAccount = (tradingAccount) => {
    const dispatch = useAppDispatch();
    const navigation = useMainStackNavigation();
    const { connectedAccount } = useAppSelector(selectConnectedAccountWithBankId);
    const [, , , fetch] = useFetch(muteAllButThrows);
    const performDirectDebitDeposit = useCallback(async (amount) => {
        if (!tradingAccount?.id) {
            return;
        }
        try {
            LoadingAppViewModal.show({ title: 'Processing your payment...' });
            const res = await fetch(topUpSecclAccountFromBankAccountUsingDirectDebit({
                amount,
                tradingAccountId: tradingAccount.id,
            }));
            dispatch(pollPendingTopupsForTradingAccountId(tradingAccount.id));
            dispatch(fetchWealthAccounts(3, tradingAccount.id));
            navigation.goBack();
            if (res && res.estimatedDateOfCollection) {
                navigation.navigate('DepositToTradingAccountTimeline', {
                    amount,
                    date: new Date(),
                    depositMethod: {
                        method: 'singleDirectDebit',
                        dateOfCollection: res.estimatedDateOfCollection,
                    },
                });
            }
            LoadingAppViewModal.hide();
        }
        catch (e) {
            Sentry.captureException(e);
            LoadingAppViewModal.hide();
        }
    }, [tradingAccount?.id]);
    const depositToAccountUsingDirectDebit = useCallback((amount) => {
        if (tradingAccount?.id) {
            if (connectedAccount?.investMandateStatus === 'INACTIVE' || connectedAccount?.investMandateStatus === 'NONE') {
                navigation.navigate('AutoinvestSelectFundingAccount', {
                    onDirectDebitSetup: () => {
                        performDirectDebitDeposit(amount);
                    },
                });
                return;
            }
            performDirectDebitDeposit(amount);
        }
    }, [connectedAccount?.investMandateStatus, tradingAccount?.id, performDirectDebitDeposit]);
    return useCallback(({ amount, depositMethod }) => {
        if (tradingAccount?.id) {
            if (connectedAccount) {
                if (depositMethod === 'openBankingDeposit') {
                    navigation.navigate('InvestCreateTopUp', {
                        amount,
                        backCount: 3,
                        reference: 'EMMA TRADING',
                        flow: 'topupSecclAccount',
                        bankAccount: connectedAccount,
                        tradingAccountId: tradingAccount.id,
                        onError: createTopUpErrorHandler(noop),
                        destination: getDestination(tradingAccount?.accountType),
                    });
                }
                else if (depositMethod === 'singleDirectDebit') {
                    depositToAccountUsingDirectDebit(amount);
                }
            }
        }
    }, [connectedAccount, depositToAccountUsingDirectDebit, navigation, tradingAccount]);
};
export default useInvestCashToTradingAccount;
